/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	Output,
	ViewChild
} from "@angular/core";
import { ButtonComponent } from "../button/button.component";
import { ProgressBarComponent } from "../progress-bar/progress-bar.component";
import { COLORS } from "../../utils/constants";
import { MaxDecimalsPipe } from "../../pipes/max-decimals.pipe";

@Component({
	selector: "globalictcc-upload-file",
	standalone: true,
	imports: [ButtonComponent, NgClass, ProgressBarComponent, MaxDecimalsPipe],
	templateUrl: "./upload-file.component.html",
	styleUrl: "./upload-file.component.scss"
})
export class UploadFileComponent {
	@ViewChild("fileUpload") public fileUpload: ElementRef<HTMLInputElement>;

	@Input() public fluid: boolean = false;
	@Input() public errorMessage?: string;
	@Input() public acceptedFilesExtensions: string[] = [];

	@Output() public handleUpload = new EventEmitter<File>();
	@Output() public handleRemove = new EventEmitter<void>();

	public isDraggingOver: boolean = false;
	public hasUploaded: boolean = false;
	public hasUploadingError: boolean = false;
	public hasUploadedWithSuccess: boolean = false;
	public uploadingPercentage: number = 0;
	public uploadedFileName: string = "No file selected";
	public colors = COLORS;

	private _dragCounter: number = 0;

	@HostListener("window:dragover", ["$event"])
	public onWindowDragOver(event: DragEvent) {
		event.preventDefault();
	}

	@HostListener("window:drop", ["$event"])
	public onWindowDrop(event: DragEvent) {
		event.preventDefault();
		this._resetDragState();
	}

	/**
	 * Triggers the file upload dialog by programmatically clicking on the file input element.
	 * This method is typically called when the user wants to upload a file.
	 */
	public onUploadFile() {
		this.fileUpload.nativeElement?.click();
	}

	/**
	 * Resets the file upload state and clears any uploaded file information.
	 *
	 * This method performs the following actions:
	 * - Resets the upload status flags (`hasUploaded`, `hasUploadingError`, `hasUploadedWithSuccess`) to `false`.
	 * - Resets the upload progress percentage (`uploadingPercentage`) to `0`.
	 * - Sets the uploaded file name (`uploadedFileName`) to "No file selected".
	 * - Clears the value of the file input element, if it exists.
	 */
	public onRemoveFile() {
		this.hasUploaded = false;
		this.hasUploadingError = false;
		this.hasUploadedWithSuccess = false;
		this.uploadingPercentage = 0;
		this.uploadedFileName = "No file selected";

		if (this.fileUpload?.nativeElement) this.fileUpload.nativeElement.value = "";

		this.handleRemove.emit();
	}

	/**
	 * Handles the file selection event from an input element.
	 *
	 * @param event - The file selection event triggered by the input element.
	 *
	 * This method extracts the selected file from the event, and if a file is selected,
	 * it assigns the file's name and blob to the `selectedFile` property.
	 */
	public onFileSelected(event: Event) {
		const file = (event.target as HTMLInputElement).files?.[0];

		this._processFileUpload(file);
	}

	/**
	 * Processes the file upload by reading the provided file and updating the upload status.
	 *
	 * @param file - The file to be uploaded. If not provided, an upload error is triggered.
	 *
	 * @remarks
	 * - Updates the `hasUploaded` flag to indicate that an upload attempt has been made.
	 * - If no file is provided, sets `hasUploadingError` to `true` and exits.
	 * - Sets the `uploadedFileName` to the name of the provided file.
	 * - Uses a `FileReader` to read the file as an array buffer.
	 * - Tracks the upload progress and updates `uploadingPercentage` accordingly.
	 * - Sets `hasUploadedWithSuccess` to `true` if the file is successfully read.
	 * - Sets `hasUploadingError` to `true` if an error occurs during file reading.
	 */
	private _processFileUpload(file?: File) {
		this.hasUploaded = true;

		if (!file) {
			this.hasUploadingError = true;
			return;
		}

		this.uploadedFileName = file.name;

		const reader = new FileReader();

		let fakeProgress = 0;
		this.uploadingPercentage = 0;

		const interval = setInterval(() => {
			fakeProgress += Math.floor(Math.random() * 10) + 5; // Add 5–14%

			if (fakeProgress >= 95) {
				clearInterval(interval);
			}

			this.uploadingPercentage = Math.min(fakeProgress, 95);
		}, 100);

		reader.onload = () => {
			clearInterval(interval);

			this.uploadingPercentage = 100;
			this.hasUploadedWithSuccess = true;

			this.handleUpload.emit(file);
		};

		reader.onerror = () => {
			clearInterval(interval);
			this.hasUploadingError = true;
		};

		reader.readAsArrayBuffer(file);
	}

	public onDragEnter(event: DragEvent): void {
		event.preventDefault();

		this._dragCounter++;
		this.isDraggingOver = true;
	}

	/**
	 * Handles the dragover event when a file is dragged over the component.
	 * Prevents the default browser behavior and sets the `isDraggingOver` flag to true.
	 *
	 * @param event - The drag event triggered when a file is dragged over the component.
	 */
	public onDragOver(event: DragEvent): void {
		event.preventDefault();
	}

	/**
	 * Handles the drag leave event when a dragged item leaves the drop zone.
	 * Prevents the default browser behavior and updates the `isDraggingOver` state to `false`.
	 *
	 * @param event - The drag leave event triggered when the dragged item leaves the element.
	 */
	public onDragLeave(event: DragEvent): void {
		event.preventDefault();

		this._dragCounter--;

		if (this._dragCounter === 0) {
			this.isDraggingOver = false;
		}
	}

	/**
	 * Handles the drop event when a file is dragged and dropped onto the component.
	 * Prevents the default browser behavior, updates the dragging state, and processes the uploaded file.
	 *
	 * @param event - The drag event containing the dropped file(s).
	 */
	public onDrop(event: DragEvent) {
		event.preventDefault();

		this._resetDragState();

		if (this.hasUploaded) return;

		const files = event.dataTransfer?.files;

		if (files && files.length > 0) {
			const file = files[0];

			this._processFileUpload(file);
		}
	}

	/**
	 * Resets the drag-and-drop state by setting the drag counter to zero
	 * and marking the dragging state as inactive.
	 *
	 * This method is typically used to clean up the drag state when
	 * a drag-and-drop operation is completed or canceled.
	 */
	private _resetDragState() {
		this._dragCounter = 0;
		this.isDraggingOver = false;
	}
}
